import { GridApi, GridColDef, GridRowId, GridRowModes } from "@mui/x-data-grid-premium";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import React from "react";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { ITableState } from "../EntityTable";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import ActionCellItem from "./ActionCellItem";

export interface DataGridAction {
    show: boolean | ((id: GridRowId, isEditMode: boolean) => boolean);
    disabled: boolean | ((id: GridRowId, isEditMode: boolean) => boolean);
    onActionClicked: (id: GridRowId, isEditMode: boolean) => void;
    icon: IconProp;
    iconColor?: string;
    label: string;
    className?: string;
}
export interface DataGridActionsColumnProps {
    setTableState: React.Dispatch<React.SetStateAction<ITableState>>;
    gridRef: React.MutableRefObject<GridApi>;
    editAction?: {
        show: boolean | ((id: GridRowId, record: any) => boolean);
        onSaveClicked?: (id: GridRowId) => void;
        onEditClicked?: (id: GridRowId) => void;
        onCancelClicked?: (id: GridRowId) => void;
    };
    deleteAction?: {
        show: boolean | ((id: GridRowId, record: any) => boolean);
        onDeleteClicked: (id: GridRowId) => void;
    };
    actions?: DataGridAction[];
    width?: any;
}

const DataGridActionsColumn = (props: DataGridActionsColumnProps): GridColDef => {
    const { setTableState, editAction, gridRef, deleteAction, actions } = props;
    const width = actions && actions.length > 3 ? 150 : 100;
    return {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: width,
        hideable: false,
        getActions: ({ id }) => getColumnItems(id),
    } as GridColDef;

    function getColumnItems(id: GridRowId) {
        const isInEditMode = gridRef.current.getRowMode(id) === GridRowModes.Edit;
        const customActions = mapToCustomActionIcons(id, actions, isInEditMode);
        return [getEditAction(id, isInEditMode), getDeleteAction(id, isInEditMode), ...customActions];
    }
    function mapToCustomActionIcons(id: GridRowId, actions: DataGridAction[] | undefined, isInEditMode: boolean) {
        if (!actions || actions.length === 0) {
            return [];
        }
        return actions.map((a) => {
            const show = typeof a.show === "function" ? a.show(id, isInEditMode) : a.show;
            if (a && show) {
                const clickHandler = (id: GridRowId) => (event: React.MouseEvent) => {
                    event.stopPropagation();
                    a.onActionClicked(id, isInEditMode);
                };
                const disabled = typeof a.disabled === "function" ? a.disabled(id, isInEditMode) : a.disabled;
                return (
                    <GridActionsCellItem
                        nonce={""}
                        title={a.label}
                        onResize={() => true}
                        onResizeCapture={() => true}
                        icon={<FontAwesomeIcon icon={a.icon} color={a.iconColor} />}
                        label={a.label}
                        onClick={clickHandler(id)}
                        color="inherit"
                        disabled={disabled}
                        className={a.className}
                    />
                );
            }
            return <></>;
        });
    }

    function getEditAction(id: GridRowId, isInEditMode: boolean) {
        if (editAction && (editAction.show === true || (typeof editAction.show === "function" && editAction.show(id, gridRef.current.getRow(id))))) {
            const handleSaveClick = (event: React.MouseEvent) => {
                event.stopPropagation();
                editAction.onSaveClicked && editAction.onSaveClicked(id);
            };

            const handleEditClick = (event: React.MouseEvent) => {
                event.stopPropagation();
                if (editAction.onEditClicked) {
                    editAction.onEditClicked(id);
                } else {
                    gridRef.current.startRowEditMode({ id });
                }
            };

            const handleCancelClick = (event: React.MouseEvent) => {
                event.stopPropagation();
                if (editAction.onCancelClicked) {
                    editAction.onCancelClicked(id);
                }
                gridRef.current.stopRowEditMode({ id, ignoreModifications: true });

                const row = gridRef.current.getRow(id);
                if (row?.isNew) {
                    setTableState((prevState) => {
                        const newRows = prevState.rows.filter((row) => row.id !== id);
                        return {
                            ...prevState,
                            total: (prevState.total -= 1),
                            rows: newRows,
                        };
                    });
                }
            };

            if (isInEditMode) {
                return (
                    <>
                        <ActionCellItem onClick={handleSaveClick} color={MirageTheme.palette.info.main} icon={MirageTheme.icons.saveIcon} label="Save" />
                        <ActionCellItem onClick={handleCancelClick} color={MirageTheme.palette.warning.main} icon={MirageTheme.icons.closeIcon} label="Cancel" />
                    </>
                );
            }
            return (
                <>
                    <ActionCellItem onClick={handleEditClick} color={MirageTheme.palette.primary.main} icon={MirageTheme.icons.editIcon} label="Edit" />
                </>
            );
        }
        return <></>;
    }

    function getDeleteAction(id: GridRowId, isInEditMode: boolean) {
        const autoGeneratedRowId = "auto-generated-group-footer-root";
        const rowIsNotAutoGenerated = id !== autoGeneratedRowId;
        if (
            rowIsNotAutoGenerated &&
            deleteAction &&
            (deleteAction.show === true || (typeof deleteAction.show === "function" && deleteAction.show(id, gridRef.current.getRow(id))))
        ) {
            const handleDeleteClick = (event: React.MouseEvent) => {
                event.stopPropagation();
                deleteAction.onDeleteClicked(id);
            };
            return !isInEditMode ? <ActionCellItem onClick={handleDeleteClick} color={MirageTheme.palette.error.main} icon={MirageTheme.icons.deleteIcon} label="Delete" /> : <></>;
        }
        return <></>;
    }
};

export default DataGridActionsColumn;
